/**
 * CMS/Word HTML’deki mutlak font-size (px/pt) değerlerini gövde bazına göre
 * göreli em’e çevirir. Oranlar korunur (ör. 12px başlık / 11px gövde);
 * okuma paneli (A+/A-) gerçek boyutu belirler.
 *
 * İç içe elemanlarda em çarpmaması için her boyut, en yakın üst mutlak
 * boyuta (veya gövde bazına) göre hesaplanır.
 */

const FONT_SIZE_DECL_RE = /font-size\s*:\s*([\d.]+)\s*(px|pt)/i;

function toPx(value, unit) {
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (String(unit).toLowerCase() === 'pt') return n * (96 / 72);
  return n;
}

function parseAbsoluteFontSize(style) {
  if (!style) return null;
  const m = style.match(FONT_SIZE_DECL_RE);
  if (!m) return null;
  return toPx(m[1], m[2]);
}

function pickBaselinePx(sizesPx) {
  if (sizesPx.length === 0) return null;
  const counts = new Map();
  for (const px of sizesPx) {
    const key = Math.round(px * 10) / 10;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  let best = sizesPx[0];
  let bestCount = 0;
  for (const [px, count] of counts) {
    if (count > bestCount || (count === bestCount && px < best)) {
      best = px;
      bestCount = count;
    }
  }
  return best;
}

function replaceFontSizeWithEm(style, em) {
  const rounded = Math.round(em * 1000) / 1000;
  if (/font-size\s*:/i.test(style)) {
    return style.replace(/font-size\s*:\s*[^;]+;?/gi, `font-size:${rounded}em;`);
  }
  return `font-size:${rounded}em;${style}`;
}

/**
 * @param {string} html
 * @returns {string}
 */
export function normalizeCmsArticleHtml(html) {
  if (!html || typeof html !== 'string') return html || '';
  if (typeof document === 'undefined') return html;

  const template = document.createElement('template');
  template.innerHTML = html;

  const sized = [];
  template.content.querySelectorAll('[style]').forEach((el) => {
    const px = parseAbsoluteFontSize(el.getAttribute('style') || '');
    if (px != null) sized.push(px);
  });
  if (sized.length === 0) return html;

  const baselinePx = pickBaselinePx(sized);
  if (!baselinePx) return html;

  const walk = (el, parentAbsPx) => {
    const style = el.getAttribute('style') || '';
    const absPx = parseAbsoluteFontSize(style);
    let nextParentAbs = parentAbsPx;

    if (absPx != null) {
      const ref = parentAbsPx ?? baselinePx;
      const em = absPx / ref;
      el.setAttribute('style', replaceFontSizeWithEm(style, em));
      nextParentAbs = absPx;
    }

    Array.from(el.children).forEach((child) => walk(child, nextParentAbs));
  };

  Array.from(template.content.children).forEach((child) => walk(child, null));
  return template.innerHTML;
}

/**
 * Word/CMS karışık font-size / font-family stillerini kaldırır.
 * İtalik, kalın, hizalama ve link alt çizgisi korunur; tipografi CSS’ten tek tip gelir.
 * @param {string} html
 * @returns {string}
 */
export function stripCmsInlineTypography(html) {
  if (!html || typeof html !== 'string') return html || '';
  if (typeof document === 'undefined') return html;

  const template = document.createElement('template');
  template.innerHTML = html;

  template.content.querySelectorAll('[style]').forEach((el) => {
    const style = el.getAttribute('style') || '';
    const kept = style
      .split(';')
      .map((part) => part.trim())
      .filter(Boolean)
      .filter((part) => {
        const prop = part.split(':')[0].trim().toLowerCase();
        return (
          prop !== 'font-size' &&
          prop !== 'font-family' &&
          prop !== 'font' &&
          prop !== 'line-height' &&
          prop !== 'letter-spacing' &&
          prop !== 'word-spacing' &&
          prop !== 'vertical-align' &&
          !prop.startsWith('mso-')
        );
      })
      .join('; ');

    if (kept) el.setAttribute('style', `${kept};`);
    else el.removeAttribute('style');
  });

  return template.innerHTML;
}

/** @deprecated Kaynakça — stripCmsInlineTypography kullanın */
export function normalizeCmsBibliographyHtml(html) {
  return stripCmsInlineTypography(html);
}
